import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const people = (n: number) => `${n.toLocaleString()} ${n === 1 ? 'person' : 'people'}`;

/**
 * Naming a call book: a new one, or a rename. Saves only a changed, non-blank name; the server says why a
 * name was refused (taken, too long), shown under the field.
 */
export function CallSheetNameDialog({
  open,
  onOpenChange,
  title,
  initialName = '',
  submitLabel,
  onSubmit,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  initialName?: string;
  submitLabel: string;
  onSubmit: (name: string) => void;
  error: string | null;
}) {
  const [name, setName] = useState(initialName);
  // Every opening starts from the current name, not what was typed last time.
  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);
  const trimmed = name.trim();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!trimmed) return;
            if (trimmed === initialName) return onOpenChange(false);
            onSubmit(trimmed);
          }}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="sr-only">Give the call book a name.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} aria-label="Call book name" placeholder="Call book name" />
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmed}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Deleting a call book, with what it does said first: its people come off it, and every call stays. */
export function DeleteCallSheetDialog({
  open,
  onOpenChange,
  name,
  size,
  onConfirm,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  size: number;
  onConfirm: () => void;
  error: string | null;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {name}?</AlertDialogTitle>
          <AlertDialogDescription>
            Deleting it takes {people(size)} off it. Their calls, outcomes and commitments stay on their own records.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(e) => {
              // Stays open until the delete lands, so a refusal can be read.
              e.preventDefault();
              onConfirm();
            }}
          >
            Delete {name}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
